import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Trophy, Check, X, Gift, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const TR = {
  sk: {
    back: "Späť na výber",
    question: "Otázka",
    of: "z",
    next: "Ďalšia",
    finish: "Dokončiť kvíz",
    yourScore: "Tvoje skóre",
    level_expert: "Expert",
    level_advanced: "Pokročilý",
    level_intermediate: "Stredný",
    level_beginner: "Začiatočník",
    levelLabel: "Úroveň",
    correct: "Správne",
    wrong: "Nesprávne",
    explanation: "Vysvetlenie",
    bonusTitle: "🎁 Bonus aktivovaný: +7 dní ACTIVE",
    bonusBody: "Pridali sme ti 7 dní ACTIVE predplatného k tvojmu účtu. Ďakujeme, že si dokončil/a kvíz!",
    bonusAlready: "Tento bonus si už raz získal/a. Skús iný kvíz z trilógie pre ďalších +7 dní ACTIVE.",
    retry: "Znova",
    share: "Zdieľať výsledok",
    shareText: "Skóroval som {score}/{total} v kvíze Tigik! 🔥 Skús ho tiež na:",
    loading: "Načítavam otázky…",
    errLoad: "Nepodarilo sa načítať otázky.",
  },
  en: {
    back: "Back to selection",
    question: "Question",
    of: "of",
    next: "Next",
    finish: "Finish quiz",
    yourScore: "Your score",
    level_expert: "Expert",
    level_advanced: "Advanced",
    level_intermediate: "Intermediate",
    level_beginner: "Beginner",
    levelLabel: "Level",
    correct: "Correct",
    wrong: "Wrong",
    explanation: "Explanation",
    bonusTitle: "🎁 Bonus unlocked: +7 days ACTIVE",
    bonusBody: "We've added 7 days of ACTIVE to your account. Thanks for completing the quiz!",
    bonusAlready: "You've already claimed this bonus. Try another quiz from the trilogy for another +7 days ACTIVE.",
    retry: "Try again",
    share: "Share result",
    shareText: "I scored {score}/{total} on Tigik quiz! 🔥 Try it too:",
    loading: "Loading questions…",
    errLoad: "Failed to load questions.",
  },
  cs: {
    back: "Zpět na výběr",
    question: "Otázka",
    of: "z",
    next: "Další",
    finish: "Dokončit kvíz",
    yourScore: "Tvé skóre",
    level_expert: "Expert",
    level_advanced: "Pokročilý",
    level_intermediate: "Středně pokročilý",
    level_beginner: "Začátečník",
    levelLabel: "Úroveň",
    correct: "Správně",
    wrong: "Špatně",
    explanation: "Vysvětlení",
    bonusTitle: "🎁 Bonus aktivován: +7 dní ACTIVE",
    bonusBody: "Přidali jsme ti 7 dní ACTIVE předplatného k účtu. Děkujeme za dokončení kvízu!",
    bonusAlready: "Tento bonus už máš. Zkus jiný kvíz z trilogie pro dalších +7 dní ACTIVE.",
    retry: "Znovu",
    share: "Sdílet výsledek",
    shareText: "Skóroval jsem {score}/{total} v kvízu Tigik! 🔥 Zkus ho také:",
    loading: "Načítám otázky…",
    errLoad: "Nepodařilo se načíst otázky.",
  },
  de: {
    back: "Zurück zur Auswahl",
    question: "Frage",
    of: "von",
    next: "Weiter",
    finish: "Quiz beenden",
    yourScore: "Dein Ergebnis",
    level_expert: "Experte",
    level_advanced: "Fortgeschritten",
    level_intermediate: "Mittelstufe",
    level_beginner: "Anfänger",
    levelLabel: "Stufe",
    correct: "Richtig",
    wrong: "Falsch",
    explanation: "Erklärung",
    bonusTitle: "🎁 Bonus freigeschaltet: +7 Tage ACTIVE",
    bonusBody: "Wir haben deinem Konto 7 Tage ACTIVE gutgeschrieben. Danke für den Abschluss des Quiz!",
    bonusAlready: "Diesen Bonus hast du bereits erhalten. Probiere ein anderes Quiz der Trilogie für weitere +7 Tage ACTIVE.",
    retry: "Erneut versuchen",
    share: "Ergebnis teilen",
    shareText: "Ich habe {score}/{total} im Tigik-Quiz erreicht! 🔥 Versuch es auch:",
    loading: "Fragen werden geladen…",
    errLoad: "Fragen konnten nicht geladen werden.",
  },
  pl: {
    back: "Powrót do wyboru",
    question: "Pytanie",
    of: "z",
    next: "Dalej",
    finish: "Zakończ quiz",
    yourScore: "Twój wynik",
    level_expert: "Ekspert",
    level_advanced: "Zaawansowany",
    level_intermediate: "Średniozaawansowany",
    level_beginner: "Początkujący",
    levelLabel: "Poziom",
    correct: "Poprawnie",
    wrong: "Błędnie",
    explanation: "Wyjaśnienie",
    bonusTitle: "🎁 Bonus aktywowany: +7 dni ACTIVE",
    bonusBody: "Dodaliśmy do Twojego konta 7 dni ACTIVE. Dzięki za ukończenie quizu!",
    bonusAlready: "Ten bonus już został odebrany. Spróbuj innego quizu z trylogii dla kolejnych +7 dni ACTIVE.",
    retry: "Spróbuj ponownie",
    share: "Udostępnij wynik",
    shareText: "Zdobyłem {score}/{total} w quizie Tigik! 🔥 Spróbuj i ty:",
    loading: "Ładuję pytania…",
    errLoad: "Nie udało się załadować pytań.",
  },
  fr: {
    back: "Retour à la sélection",
    question: "Question",
    of: "sur",
    next: "Suivante",
    finish: "Terminer le quiz",
    yourScore: "Ton score",
    level_expert: "Expert",
    level_advanced: "Avancé",
    level_intermediate: "Intermédiaire",
    level_beginner: "Débutant",
    levelLabel: "Niveau",
    correct: "Correct",
    wrong: "Incorrect",
    explanation: "Explication",
    bonusTitle: "🎁 Bonus activé : +7 jours ACTIVE",
    bonusBody: "Nous avons ajouté 7 jours d'abonnement ACTIVE à ton compte. Merci d'avoir terminé le quiz !",
    bonusAlready: "Tu as déjà obtenu ce bonus. Essaie un autre quiz de la trilogie pour gagner +7 jours ACTIVE supplémentaires.",
    retry: "Réessayer",
    share: "Partager le résultat",
    shareText: "J'ai obtenu {score}/{total} au quiz Tigik ! 🔥 Essaie-le toi aussi sur :",
    loading: "Chargement des questions…",
    errLoad: "Impossible de charger les questions.",
  },
};

export default function Quiz() {
  const { quizType } = useParams();
  const { lang } = useLang();
  const { user, loading: authLoading } = useAuth();
  const t = TR[lang] || TR.sk;

  const [session, setSession] = React.useState(null);
  const [answers, setAnswers] = React.useState([]);
  const [current, setCurrent] = React.useState(0);
  const [selected, setSelected] = React.useState(null);
  const [result, setResult] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);

  const loadQuiz = React.useCallback(() => {
    if (!user) return;
    setSession(null);
    setAnswers([]);
    setCurrent(0);
    setSelected(null);
    setResult(null);
    api.get(`/quiz/${quizType}/start?lang=${lang}`)
      .then(({ data }) => {
        if (data?.questions?.length) {
          setSession(data);
          setAnswers(new Array(data.questions.length).fill(-1));
        }
      })
      .catch(() => toast.error(t.errLoad));
  }, [quizType, t.errLoad, lang, user]);

  React.useEffect(() => { loadQuiz(); }, [loadQuiz]);

  const submit = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/quiz/submit`, {
        session_token: session.session_token,
        answers: finalAnswers,
        lang,
      });
      if (data?.score !== undefined) setResult(data);
      else toast.error(t.errLoad);
    } catch {
      toast.error(t.errLoad);
    } finally { setSubmitting(false); }
  };

  const share = async () => {
    const text = t.shareText.replace("{score}", result.score).replace("{total}", result.total);
    const url = `${window.location.origin}/quiz/${quizType}`;
    if (navigator.share) {
      try { await navigator.share({ title: "Tigik Quiz", text, url }); } catch { /* user cancelled */ }
    } else {
      navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Link copied!");
    }
  };

  // Auth gate — quiz is FREE but only for registered users.
  if (!authLoading && !user) {
    return <Navigate to="/quiz" replace />;
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-black text-zinc-400 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin mr-2 text-gold-400" /> {t.loading}
      </div>
    );
  }

  // ============== RESULT VIEW ==============
  if (result) {
    return (
      <div className="min-h-screen bg-black text-zinc-200" data-testid="quiz-result">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <Link to="/quiz" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-gold-400 mb-8">
            <ArrowLeft className="w-3 h-3" /> {t.back}
          </Link>

          <div className="text-center py-8 px-6 border border-gold-700/40 bg-gradient-to-br from-gold-950/40 to-zinc-950 mb-8">
            <Trophy className="w-12 h-12 mx-auto text-gold-400 mb-3" />
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold-500 mb-2">{t.yourScore}</div>
            <div className="font-display font-black text-6xl text-white mb-1">
              {result.score}<span className="text-3xl text-zinc-500">/{result.total}</span>
            </div>
            <div className="text-zinc-400 text-sm">{result.pct}%</div>
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 border border-gold-700/40 text-gold-400 text-xs font-mono uppercase tracking-widest">
              {t.levelLabel}: {t[`level_${result.level}`]}
            </div>
            <div className="mt-5">
              <button onClick={share} className="inline-flex items-center gap-2 px-4 py-2 border border-gold-700/40 text-gold-300 hover:bg-gold-900/20 text-xs font-mono uppercase tracking-wider" data-testid="quiz-share-btn">
                <Share2 className="w-3 h-3" /> {t.share}
              </button>
            </div>
          </div>

          <div className="border border-gold-700/40 bg-gradient-to-br from-gold-950/30 to-zinc-950 p-6 mb-8" data-testid="quiz-bonus-banner">
            <div className="flex items-start gap-3">
              <Gift className="w-6 h-6 text-gold-400 mt-1 shrink-0" />
              <div>
                <h3 className="font-display font-bold text-xl text-white mb-1">
                  {result.bonus_granted ? t.bonusTitle : "🏆 " + t.bonusTitle.replace("🎁 ", "")}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  {result.bonus_granted ? t.bonusBody : t.bonusAlready}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {result.details.map((d, i) => (
              <div key={i} className={`p-4 border ${d.is_correct ? "border-emerald-700/40 bg-emerald-950/10" : "border-rose-700/40 bg-rose-950/10"}`} data-testid={`quiz-result-${i}`}>
                <div className="flex items-start gap-2 mb-2">
                  {d.is_correct ? <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> : <X className="w-4 h-4 text-rose-400 mt-0.5 shrink-0" />}
                  <p className="text-sm font-medium text-zinc-100">{d.q}</p>
                </div>
                <div className="ml-6 space-y-1 text-xs">
                  {d.options.map((opt, j) => (
                    <div key={j} className={`flex items-center gap-2 ${j === d.correct_answer ? "text-emerald-300 font-medium" : j === d.your_answer && !d.is_correct ? "text-rose-300 line-through" : "text-zinc-500"}`}>
                      <span className="w-4">{j === d.correct_answer ? "✓" : j === d.your_answer ? "✗" : "·"}</span>
                      <span>{opt}</span>
                    </div>
                  ))}
                  {d.explain && (
                    <div className="mt-2 pt-2 border-t border-zinc-800/60 text-zinc-400 italic">
                      💡 <strong className="text-zinc-300">{t.explanation}:</strong> {d.explain}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <button onClick={loadQuiz} className="px-6 py-3 border border-gold-700/40 text-gold-300 hover:bg-gold-900/20 font-mono uppercase tracking-wider text-xs" data-testid="quiz-retry-btn">
              {t.retry}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============== QUIZ VIEW ==============
  const q = session.questions[current];
  const isLast = current === session.questions.length - 1;
  const goNext = () => {
    if (selected === null) return;
    const newAns = [...answers]; newAns[current] = selected; setAnswers(newAns);
    if (isLast) {
      submit(newAns);
    } else {
      setCurrent(current + 1);
      setSelected(null);
    }
  };

  return (
    <div className="min-h-screen bg-black text-zinc-200" data-testid="quiz-active">
      <div className="max-w-2xl mx-auto px-6 py-12">
        <Link to="/quiz" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-gold-400 mb-8">
          <ArrowLeft className="w-3 h-3" /> {t.back}
        </Link>

        {/* Progress */}
        <div className="flex items-center justify-between text-xs font-mono uppercase tracking-wider text-zinc-500 mb-4">
          <span>{t.question} {current + 1} {t.of} {session.questions.length}</span>
          <span className="text-gold-400">{Math.round(((current) / session.questions.length) * 100)}%</span>
        </div>
        <div className="h-1 bg-zinc-900 mb-8">
          <div className="h-full bg-gold-500 transition-all" style={{ width: `${((current) / session.questions.length) * 100}%` }} />
        </div>

        <h2 className="font-display font-bold text-xl sm:text-2xl text-white mb-6 leading-snug">{q.q}</h2>

        <div className="space-y-3">
          {q.options.map((opt, i) => (
            <button
              key={i}
              onClick={() => setSelected(i)}
              data-testid={`quiz-option-${i}`}
              className={`w-full text-left px-4 py-3.5 border transition-all text-sm ${
                selected === i
                  ? "border-gold-500 bg-gold-950/40 text-white"
                  : "border-zinc-800 hover:border-zinc-700 text-zinc-300"
              }`}
            >
              <span className="font-mono text-xs text-zinc-600 mr-3">{String.fromCharCode(65 + i)}.</span>
              {opt}
            </button>
          ))}
        </div>

        <button
          onClick={goNext}
          disabled={selected === null || submitting}
          className="mt-8 w-full bg-gold-500 hover:bg-gold-400 disabled:bg-zinc-800 disabled:text-zinc-600 text-black font-mono uppercase tracking-wider text-xs font-bold py-4 transition-colors inline-flex items-center justify-center gap-2"
          data-testid="quiz-next-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : isLast ? t.finish : t.next}
          {!submitting && <ArrowRight className="w-3 h-3" />}
        </button>
      </div>
    </div>
  );
}
